"use client";

import { RefObject, useEffect } from "react";
import {
  deriveSurface,
  isOnScreen,
  useLogEvent,
  useOnVisible,
} from "@repo/statsig";

/**
 * Last route that logged `content_visible`. `FeltEmbeddedComponent` renders
 * several times on a page (the app-wide host, nav header, cards) and every
 * instance observes layout, so without this the same impression fires once per
 * instance. Keyed by route rather than a boolean so returning to a page counts
 * as a new impression.
 */
let contentVisibleRoute: string | null = null;

/** How much visible time one follow-up checkpoint accounts for. */
const CHECKPOINT_INTERVAL_MS = 20_000;

/** Shared across hosts so one interval elapsing yields one checkpoint, not one per host. */
let lastCheckpointAt = 0;

/**
 * Log `content_visible` the first time a page is actually on screen, once per
 * route. Rides the shared {@link useOnVisible} signal — the same visibility gate
 * `screen_viewed` uses.
 *
 * Previously bolted onto `useResizePublisher`, which only shared the
 * `ResizeObserver` as a "layout changed → re-check visibility" tick and is
 * otherwise unrelated (its job is publishing `layoutUpdated` to native). Split
 * out so each hook owns one concern.
 */
export function useContentVisible(ref: RefObject<HTMLElement | null>): void {
  const log = useLogEvent();

  useOnVisible(
    () => {
      // Read at fire time: this component lives in the root layout and never
      // remounts, so a route captured at arm time would go stale on the first
      // client-side navigation.
      const route = window.location.pathname;
      if (contentVisibleRoute === route) return;
      contentVisibleRoute = route;
      log("content_visible", route, {
        route,
        surface: deriveSurface(route),
      });
    },
    { ref }
  );

  // Re-emit on a timer while the page stays on screen, so the event carries
  // duration as well as the impression. `elapsed_ms` is the fixed interval
  // rather than a wall-clock delta, so a suspended webview resuming cannot
  // book its time asleep as time on screen.
  useEffect(() => {
    const id = setInterval(() => {
      const now = Date.now();
      if (!isOnScreen() || now - lastCheckpointAt < CHECKPOINT_INTERVAL_MS)
        return;
      lastCheckpointAt = now;
      const route = window.location.pathname;
      log("content_visible", route, {
        route,
        surface: deriveSurface(route),
        elapsed_ms: CHECKPOINT_INTERVAL_MS,
      });
    }, CHECKPOINT_INTERVAL_MS);
    return () => clearInterval(id);
  }, [log]);
}

export default useContentVisible;
